import { ArrowLeftOutlined } from '@ant-design/icons';
import { Skeleton } from 'antd';
import Button from 'components/Button';
import { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { routes } from 'routes/AllRoutes';

import DrawerComponent from 'components/Drawer';
import FarmerGroupContent from 'screens/farmer-groups/components/drawer/FarmerGroupContent';
import FarmerGroupLeaderContent from './FarmerGroupLeaderContent';
import PaddyBuyerContent from './PaddyBuyerContent';

import useFarmerGroupById from 'screens/farmer-groups/hooks/useFarmerGroupByIdQuery';
import MachineOwnerContent from './MachineOwnerContent';

import { useTranslation } from 'react-i18next';
import type { MachineOwner } from 'screens/stakeholders/hooks/queries/useMachineOwnerQuery';
import type { FarmerGroupLeader, PaddyBuyer } from 'types/common';
import ProtectedResource from 'components/RoleBasedAccessControl/ProtectedResource';
import { PermissionKey } from 'types/rbac';

type StakeholderConfigItem = {
  label: string;
  permissionKey: PermissionKey;
  routeKey: keyof typeof routes;
};

const stakeholderConfig: Record<string, StakeholderConfigItem> = {
  paddy_buyer: {
    label: 'stakeholders.farmerStakeholderDetails',
    permissionKey: 'paddy_buyer:edit',
    routeKey: 'paddyBuyerEdit',
  },
  farmer_group_leader: {
    label: 'stakeholders.farmerGroupLeaderDetails',
    permissionKey: 'farmer_group_leader:edit',
    routeKey: 'farmerGroupLeaderEdit',
  },
  machine_owner: {
    label: 'stakeholders.machineOwnerDetails',
    permissionKey: 'machine_owner:edit',
    routeKey: 'machineOwnerEdit',
  },
};

type StakeholderDrawerProps = {
  country: string;
  data?: FarmerGroupLeader | PaddyBuyer | MachineOwner;
  open: boolean;
  onClose: () => void;
  isOpenFarmerGroupDetail: boolean;
  setIsOpenFarmerGroupDetail: (value: boolean) => void;
};

const StakeholderDetailDrawer = ({
  country,
  data,
  open,
  onClose,
  isOpenFarmerGroupDetail,
  setIsOpenFarmerGroupDetail,
}: StakeholderDrawerProps) => {
  const { t } = useTranslation();
  const [selectedFarmerGroupId, setSelectedFarmerGroupId] = useState('');
  const [activeKey, setActiveKey] = useState('1');
  const location = useLocation();
  const isPaddyBuyer =
    location.pathname === routes.paddyBuyer ||
    location.pathname === routes.stakeholders;

  const getStakeholderConfig = (
    pathname: string,
  ): StakeholderConfigItem | null => {
    switch (pathname) {
      case routes.paddyBuyer:
        return stakeholderConfig.paddy_buyer;
      case routes.farmerGroupLeader:
        return stakeholderConfig.farmer_group_leader;
      case routes.machineOwner:
        return stakeholderConfig.machine_owner;
      default:
        return stakeholderConfig.paddy_buyer;
    }
  };

  const currentConfig = useMemo(() => {
    return getStakeholderConfig(location.pathname);
  }, [location.pathname]);

  const { data: farmerGroupData, isLoading: isLoadingFarmerGroup } =
    useFarmerGroupById(
      selectedFarmerGroupId,
      !!selectedFarmerGroupId && isPaddyBuyer,
    );

  const renderDrawerContent = () => {
    if (isOpenFarmerGroupDetail && isLoadingFarmerGroup)
      return <Skeleton active />;

    if (isPaddyBuyer) {
      if (isOpenFarmerGroupDetail)
        return (
          <FarmerGroupContent
            country={country}
            data={farmerGroupData?.data}
            activeKey={activeKey}
            setActiveKey={setActiveKey}
          />
        );
      return (
        <PaddyBuyerContent
          country={country}
          data={data as PaddyBuyer}
          activeKey={activeKey}
          setActiveKey={setActiveKey}
          onClickFarmerGroup={(id: string) => {
            setIsOpenFarmerGroupDetail(true);
            setSelectedFarmerGroupId(id);
            setActiveKey('1');
          }}
        />
      );
    }

    if (location.pathname === routes.farmerGroupLeader) {
      return (
        <FarmerGroupLeaderContent
          activeKey={activeKey}
          setActiveKey={setActiveKey}
          data={data as FarmerGroupLeader}
        />
      );
    }

    if (location.pathname === routes.machineOwner) {
      return (
        <MachineOwnerContent
          data={data as MachineOwner}
          activeKey={activeKey}
          setActiveKey={setActiveKey}
        />
      );
    }
  };

  return (
    <DrawerComponent
      title={
        isPaddyBuyer
          ? isOpenFarmerGroupDetail
            ? t('stakeholders.farmerGroupDetails')
            : t('stakeholders.farmerStakeholderDetails')
          : t(currentConfig?.label ?? '')
      }
      open={open}
      onClose={() => {
        onClose();
        if (!isOpenFarmerGroupDetail) setActiveKey('1');
        else setActiveKey('2');
      }}
      extra={
        !isOpenFarmerGroupDetail ? (
          <ProtectedResource permission={currentConfig?.permissionKey}>
            <Link
              to={
                currentConfig?.routeKey
                  ? routes?.[currentConfig?.routeKey]?.replace(
                      ':id',
                      data?.id ?? '',
                    )
                  : ''
              }
            >
              <Button>{t('common.edit', { value: '' })}</Button>
            </Link>
          </ProtectedResource>
        ) : null
      }
      content={renderDrawerContent()}
      closeIcon={isOpenFarmerGroupDetail && <ArrowLeftOutlined />}
    />
  );
};

export default StakeholderDetailDrawer;
